import { Component, effect, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { BuildingView, CompanyBuildingView, CompanyView, SearchCompaniesForBuildingParameters } from '@api-proxies';
import { CompanyBuildingService } from '@http/clients/company-building.service';
import { CompanyService } from '@http/clients/company.service';
import { ITypeAheadDropdownEntityParameters } from '@interfaces/type-ahead-dropdown-entity-parameters.interface';
import { EntityTypes } from '@models/enums/EntityTypes';
import { AwaiterService } from '@services/awaiter.service';
import { GridService } from '@services/grid.service';
import { IKebabAction } from '@shared/grids/custom-cell-actions/custom-cell-actions.component';
import { ServerSideGridComponent } from '@shared/grids/server-side-grid/server-side-grid.component';
import { ModalConfirmComponent } from '@shared/modals/modal-confirm/modal-confirm.component';
import { ModalEditAddressComponent } from '@shared/modals/modal-edit-address/modal-edit-address.component';
import { TypeAheadCompaniesComponent } from '@shared/type-ahead-companies/type-ahead-companies.component';
import { ColDef, IServerSideDatasource } from 'ag-grid-enterprise';
import { ModalService, SnackbarNotificationService } from 'ngx-lib';
import { BUILDING_COMPANIES_GRID_COLUMNS } from './buildings-single-companies.config';

@Component({
    selector: 'app-buildings-single-companies',
    templateUrl: './buildings-single-companies.component.html',
    styleUrls: ['./buildings-single-companies.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ServerSideGridComponent, TypeAheadCompaniesComponent]
})
export class BuildingsSingleCompaniesComponent {
    building = input<BuildingView>();

    tabCounterChange = output<void>();

    companiesDatasource?: IServerSideDatasource;
    gridColumns: ColDef[] = [...BUILDING_COMPANIES_GRID_COLUMNS];
    noResults = signal<boolean>(false);
    isLoading = signal<boolean>(false);
    typeAheadSearchByEntityParameters: ITypeAheadDropdownEntityParameters = {
        entityKeyName: EntityTypes.Building
    };

    private readonly companyService = inject(CompanyService);
    private readonly companyBuildingService = inject(CompanyBuildingService);
    private readonly awaiter = inject(AwaiterService);
    private readonly modalService = inject(ModalService);
    private readonly snackbarNotificationService = inject(SnackbarNotificationService);
    private readonly gridService = inject(GridService);

    constructor() {
        this.setupGridColumns();

        effect(() => {
            const building = this.building();
            const buildingId = building?.id;
            const organizationId = building?.organizationId;

            if (!buildingId) return;

            this.typeAheadSearchByEntityParameters = {
                entityKeyName: EntityTypes.Building,
                entity: {
                    buildingId,
                    organizationId
                }
            };

            if (!this.companiesDatasource) {
                this.createDatasource();
                return;
            }

            this.refreshGridView();
        });
    }

    getRowIdFn = (params: any) => params.data?.companyId?.toString();

    async onSelectedCompany(company: CompanyView): Promise<void> {
        const buildingId = this.building()?.id;
        if (!company || !buildingId) return;

        try {
            const companySaved = await this.companyBuildingService.save({
                companyId: company.id,
                buildingId
            } as CompanyBuildingView);

            if (companySaved) {
                this.tabCounterChange.emit();
                const gridApi = this.gridService.getGridApi();
                let applied = false;

                if (gridApi?.applyServerSideTransaction) {
                    const transactionResult = gridApi.applyServerSideTransaction({
                        add: [companySaved]
                    });
                    applied = transactionResult?.status === 'Applied';
                }

                if (!applied) {
                    this.refreshGridView();
                }

                this.noResults.set(false);
            }
        } catch {
            this.snackbarNotificationService.error('An error occurred while trying to save the company');
        }
    }

    async editAddress(companyBuilding: CompanyBuildingView): Promise<void> {
        if (!companyBuilding) return;

        const updatedCompanyBuilding = (await this.modalService.open(ModalEditAddressComponent, {
            companyBuilding: companyBuilding
        })) as CompanyBuildingView;

        if (updatedCompanyBuilding) {
            const gridApi = this.gridService.getGridApi();
            if (!gridApi) return;

            const node = gridApi.getRowNode(updatedCompanyBuilding.companyId.toString());
            if (node?.data) {
                Object.assign(node.data, updatedCompanyBuilding);
                gridApi.redrawRows({ rowNodes: [node] });
            }
        }
    }

    async setAsMainOffice(companyBuilding: CompanyBuildingView) {
        if (!companyBuilding) return;

        const company = await this.companyService.getById(companyBuilding.companyId);

        if (company.mainOfficeId) {
            const responseOk = await this.modalService.open(ModalConfirmComponent, {
                acceptCaption: 'Confirm',
                cancelCaption: 'Cancel',
                content:
                    "You're about to set a new main office for this company. This will replace your current main office with the selected one. Do you wish to continue?",
                title: 'New Main Office'
            });

            if (!responseOk) return;
        }

        await this.awaiter.awaitAction(
            'Setting as Main Office',
            async () => {
                await this.companyBuildingService.setAsMain({
                    companyId: companyBuilding.companyId,
                    buildingId: this.building()?.id,
                    line2: companyBuilding.line2
                } as CompanyBuildingView);

                this.setMainOfficeInGrid(companyBuilding.companyId);
            },
            (loading) => this.isLoading.set(loading)
        );
    }

    async unsetAsMainOffice(companyBuilding: CompanyBuildingView): Promise<void> {
        if (!companyBuilding) return;

        await this.awaiter.awaitAction(
            'Unsetting as Main Office',
            async () => {
                await this.companyBuildingService.unsetAsMain(companyBuilding);
                this.unsetMainOfficeInGrid(companyBuilding.companyId);
            },
            (loading) => this.isLoading.set(loading)
        );
    }

    async removeItem(companyBuilding: CompanyBuildingView): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Remove company',
            cancelCaption: 'Cancel',
            content: 'Are you sure you want to remove this company from the company list?',
            title: 'Remove company'
        });

        if (!responseOk) return;

        await this.awaiter.awaitAction(
            'Removing company',
            async () => {
                await this.companyBuildingService.remove({
                    companyId: companyBuilding.companyId,
                    buildingId: this.building()?.id
                } as CompanyBuildingView);

                this.tabCounterChange.emit();

                const gridApi = this.gridService.getGridApi();
                let removed = false;

                if (gridApi?.applyServerSideTransaction) {
                    const node = gridApi.getRowNode(companyBuilding.companyId.toString());
                    if (node?.data) {
                        const transactionResult = gridApi.applyServerSideTransaction({
                            remove: [node.data]
                        });
                        removed = transactionResult?.status === 'Applied';
                    }
                }

                if (removed && gridApi) {
                    this.noResults.set(gridApi.getDisplayedRowCount() === 0);
                } else {
                    this.refreshGridView();
                }
            },
            (loading) => this.isLoading.set(loading)
        );
    }

    async refreshGridView() {
        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        gridApi.refreshServerSide({ purge: true });
    }

    private setupGridColumns(): void {
        const actions: IKebabAction[] = [
            {
                label: 'Edit Address Line 2',
                action: (companyBuilding: CompanyBuildingView) => this.editAddress(companyBuilding)
            },
            {
                label: 'Set as Main Office',
                action: (companyBuilding: CompanyBuildingView) => this.setAsMainOffice(companyBuilding),
                show: (companyBuilding: CompanyBuildingView) => !companyBuilding.isMain
            },
            {
                label: 'Unset as Main Office',
                action: (companyBuilding: CompanyBuildingView) => this.unsetAsMainOffice(companyBuilding),
                show: (companyBuilding: CompanyBuildingView) => companyBuilding.isMain
            },
            {
                label: 'Remove',
                action: (companyBuilding: CompanyBuildingView) => this.removeItem(companyBuilding)
            }
        ];

        this.gridColumns = BUILDING_COMPANIES_GRID_COLUMNS.map((col) => {
            if (col.colId === 'actions') {
                return {
                    ...col,
                    cellRendererParams: {
                        actions
                    }
                };
            }
            return col;
        });
    }

    private searchParamsBuilder(gridParams: any): SearchCompaniesForBuildingParameters {
        const { startRow, sortModel } = gridParams;
        const pageSize = 20;
        const pageNumber = Math.floor(startRow / pageSize) + 1;

        const buildingId = this.building()?.id;
        if (!buildingId) {
            throw new Error('Building is required to search companies for building.');
        }
        const searchParams = {
            pageNumber,
            pageSize,

            ...(sortModel?.length && sortModel[0].sort
                ? {
                      sortBy: sortModel[0].colId,
                      sortDirection: sortModel[0].sort === 'asc' ? 'A' : 'D'
                  }
                : {}),
            buildingId: buildingId
        } as SearchCompaniesForBuildingParameters;

        return searchParams;
    }

    private createDatasource(): void {
        this.companiesDatasource = {
            getRows: async (params: any) => {
                try {
                    this.isLoading.set(true);

                    const searchParams = this.searchParamsBuilder(params.request);

                    const response = await this.companyService.searchForBuilding(searchParams);

                    const rowData = response?.results ?? [];
                    const totalCount = response?.pageInfo?.itemsCount ?? 0;

                    this.noResults.set(rowData.length === 0 && params.request.startRow === 0);

                    params.success({
                        rowData,
                        rowCount: totalCount
                    });
                } catch (error) {
                    console.error('Error fetching companies:', error);
                    params.fail();
                    this.noResults.set(true);
                    this.snackbarNotificationService.error('An error occurred while trying to load the companies');
                } finally {
                    this.isLoading.set(false);
                }
            }
        };
    }

    private unsetMainOfficeInGrid(companyId?: number): void {
        if (!companyId) return;

        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        const node = gridApi.getRowNode(companyId.toString());
        if (node?.data) {
            node.data.isMain = false;
            gridApi.redrawRows({ rowNodes: [node] });
        }
    }

    private setMainOfficeInGrid(newMainCompanyId?: number): void {
        if (!newMainCompanyId) return;

        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        const rowsToUpdate: any[] = [];

        const newMainNode = gridApi.getRowNode(newMainCompanyId.toString());
        if (newMainNode?.data) {
            newMainNode.data.isMain = true;
            rowsToUpdate.push(newMainNode);
        }

        if (rowsToUpdate.length > 0) {
            gridApi.redrawRows({ rowNodes: rowsToUpdate });
        }
    }
}
